import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Plus, Loader2, Eye, MousePointerClick, Target, Users, DollarSign, Megaphone, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { motion } from "framer-motion";
import { toast } from "sonner";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-none transition-all";

function Stat({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-5">
      <div className={`h-10 w-10 rounded-xl ${tint} flex items-center justify-center mb-3`}><Icon className="h-5 w-5" strokeWidth={2.4} /></div>
      <p className="text-2xl font-extrabold font-display text-slate-900">{value}</p>
      <p className="text-sm text-slate-400 font-medium">{label}</p>
    </div>
  );
}

export default function Campaigns() {
  const { user } = useAuth();
  const isBrand = user.role === "brand";
  const [campaigns, setCampaigns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", budget: "", category: "", deliverables: "", cover: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [{ data: c }] = await Promise.all([api.get("/campaigns")]);
    setCampaigns(c);
    if (isBrand) { const { data: a } = await api.get("/campaigns/analytics/summary"); setAnalytics(a); }
    setLoading(false);
  }, [isBrand]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title || !form.budget) return toast.error("Title and budget required");
    setSaving(true);
    try {
      await api.post("/campaigns", { ...form, budget: parseFloat(form.budget) });
      toast.success("Campaign launched! 🚀");
      setOpen(false); setForm({ title: "", description: "", budget: "", category: "", deliverables: "", cover: "" });
      await load();
    } finally { setSaving(false); }
  };

  const apply = async (id) => {
    try {
      await api.post(`/campaigns/${id}/apply`);
      toast.success("Applied! The brand will be in touch.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-pink-500" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Campaigns</h1>
          <p className="text-slate-500">{isBrand ? "Launch campaigns and track performance." : "Browse brand campaigns and apply."}</p>
        </div>
        {isBrand && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="new-campaign-btn" className="flex items-center gap-2 px-5 py-3 rounded-full font-bold text-white brand-gradient shadow-lg shadow-pink-500/30 hover:-translate-y-0.5 transition-all">
                <Plus className="h-5 w-5" /> New Campaign
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-lg">
              <DialogHeader><DialogTitle className="font-display text-xl">Launch a campaign</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <input data-testid="campaign-title" placeholder="Campaign title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                <textarea data-testid="campaign-desc" placeholder="Describe what you're looking for…" rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="campaign-budget" type="number" placeholder="Budget ($)" className={inputCls} value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
                  <input data-testid="campaign-category" placeholder="Category" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                </div>
                <input data-testid="campaign-deliverables" placeholder="Deliverables (e.g. 2 Reels + 3 Stories)" className={inputCls} value={form.deliverables} onChange={(e) => setForm({ ...form, deliverables: e.target.value })} />
                <input data-testid="campaign-cover" placeholder="Cover image URL (optional)" className={inputCls} value={form.cover} onChange={(e) => setForm({ ...form, cover: e.target.value })} />
                <button data-testid="campaign-save" onClick={create} disabled={saving} className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-bold text-white brand-gradient shadow-md disabled:opacity-50">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} Launch campaign
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isBrand && analytics && (
        <div className="mb-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <Stat icon={Eye} label="Impressions" value={analytics.impressions.toLocaleString()} tint="bg-blue-100 text-blue-600" />
            <Stat icon={MousePointerClick} label={`Clicks · ${analytics.ctr}% CTR`} value={analytics.clicks.toLocaleString()} tint="bg-pink-100 text-pink-600" />
            <Stat icon={Target} label="Conversions" value={analytics.conversions.toLocaleString()} tint="bg-fuchsia-100 text-fuchsia-600" />
            <Stat icon={Users} label="Applicants" value={analytics.applicants} tint="bg-rose-100 text-rose-600" />
          </div>
          {analytics.per_campaign.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6">
              <h3 className="font-display font-bold text-lg mb-4 text-slate-900">Performance by campaign</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={analytics.per_campaign}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #fbcfe8" }} />
                  <Bar dataKey="impressions" fill="#ec4899" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="clicks" fill="#d946ef" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="conversions" fill="#2563eb" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      <h2 className="font-display font-bold text-xl mb-4 text-slate-900">{isBrand ? "All campaigns" : "Open campaigns"}</h2>
      {campaigns.length === 0 ? (
        <div className="text-center py-16 text-slate-400"><Megaphone className="h-10 w-10 mx-auto mb-3 text-pink-300" /><p className="font-semibold">No campaigns yet.</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {campaigns.map((c, i) => {
            const applied = c.applicants.some((a) => a.user_id === user.id);
            return (
              <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden hover:-translate-y-1 transition-all duration-200" data-testid="campaign-card">
                {c.cover ? <img src={c.cover} alt={c.title} className="h-36 w-full object-cover" /> : <div className="h-36 brand-gradient" />}
                <div className="p-5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="px-2.5 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 text-[10px] font-bold uppercase">{c.category || "General"}</span>
                    <span className="flex items-center gap-1 font-extrabold text-slate-900"><DollarSign className="h-4 w-4 text-green-500" />{c.budget.toLocaleString()}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg mt-2 text-slate-900">{c.title}</h3>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{c.description}</p>
                  <p className="text-xs text-slate-400 mt-2">by {c.brand_name} · {c.deliverables}</p>
                  {isBrand ? (
                    <p className="mt-4 text-sm font-semibold text-pink-600">{c.applicants.length} applicant{c.applicants.length !== 1 ? "s" : ""}</p>
                  ) : (
                    <button data-testid={`apply-btn-${c.id}`} onClick={() => apply(c.id)} disabled={applied}
                      className={`mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-full font-bold text-sm transition-all ${applied ? "bg-green-100 text-green-700" : "brand-gradient text-white shadow-md shadow-pink-500/30 hover:-translate-y-0.5"}`}>
                      {applied ? <><Check className="h-4 w-4" /> Applied</> : "Apply now"}
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
